import { motion } from "framer-motion";
import { Crown, RotateCcw, Trash2, UserPlus, X } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";
import { EXTRA_SWATCHES, initials, TEAM_PALETTE } from "./data.js";
import { cn } from "./cn.js";

export function MemberPanel({
  node,
  nodes,
  teams,
  byId,
  rootIds,
  onAddRoot,
  focusToken,
  descendantIds,
  onClose,
  onUpdate,
  onAddChild,
  onRemove,
  onReparent,
  onSelect,
  isLight,
  C,
}) {
  const nameRef = useRef(null);
  const isRoot = rootIds.has(node.id);
  const isOnlyRoot = isRoot && rootIds.size <= 1;
  const parent = node.parentId ? byId.get(node.parentId) : undefined;
  const reportCount = useMemo(
    () => nodes.filter((n) => n.parentId === node.id).length,
    [nodes, node.id]
  );
  const reparentOptions = useMemo(
    () =>
      nodes.filter(
        (n) => n.id !== node.id && !descendantIds.has(n.id) && n.id !== node.parentId
      ),
    [nodes, node.id, node.parentId, descendantIds]
  );

  useEffect(() => {
    nameRef.current?.focus();
    nameRef.current?.select();
  }, [focusToken]);

  const surfaceBg = isLight ? "rgba(255,255,255,0.9)" : "rgba(13,14,18,0.88)";
  const surfaceBorder = isLight ? "rgba(15,23,42,0.1)" : "rgba(255,255,255,0.09)";
  const subBorder = isLight ? "rgba(15,23,42,0.08)" : "rgba(255,255,255,0.06)";
  const mute1 = isLight ? "rgba(15,23,42,0.45)" : "rgba(255,255,255,0.4)";
  const mute2 = isLight ? "rgba(15,23,42,0.35)" : "rgba(255,255,255,0.25)";
  const textPrimary = isLight ? "rgba(15,23,42,0.9)" : "rgba(255,255,255,0.9)";
  const chipInactiveBg = isLight ? "rgba(15,23,42,0.04)" : "rgba(255,255,255,0.03)";
  const hoverMuteBg = isLight ? "rgba(15,23,42,0.06)" : "rgba(255,255,255,0.07)";
  const selectBg = isLight ? "#ffffff" : "#14151a";
  const inputBg = isLight ? "#ffffff" : "rgba(255,255,255,0.04)";
  const inputBgFocus = isLight ? "#ffffff" : "rgba(255,255,255,0.06)";
  const ringOffset = isLight ? "#ffffff" : "#0d0e12";

  return (
    <motion.aside
      key={node.id}
      initial={{ x: "110%", opacity: 0 }}
      animate={{ x: "0%", opacity: 1 }}
      exit={{ x: "110%", opacity: 0 }}
      transition={{ type: "spring", stiffness: 220, damping: 26 }}
      className="absolute right-5 top-[72px] z-40 flex w-[min(312px,calc(100%-2.5rem))] max-w-sm flex-col overflow-hidden rounded-2xl border backdrop-blur-2xl"
      style={{
        bottom: "clamp(5.5rem, calc(5rem + 40px), 7rem)",
        maxHeight: "calc(100% - 72px - clamp(5.5rem, calc(5rem + 40px), 7rem))",
        background: surfaceBg,
        borderColor: surfaceBorder,
        boxShadow: isLight
          ? "0 30px 80px -20px rgba(15,23,42,0.25)"
          : "0 30px 80px -20px rgba(0,0,0,0.9)",
      }}
    >
      <div
        className="flex items-center justify-between border-b px-5 py-3.5"
        style={{ borderColor: subBorder }}
      >
        <span
          className="font-mono text-[9.5px] uppercase tracking-[0.3em]"
          style={{ color: mute1 }}
        >
          Member Detail
        </span>
        <button
          onClick={onClose}
          aria-label="Close panel"
          className="flex h-6 w-6 items-center justify-center rounded-md transition-colors"
          style={{ color: mute1 }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = hoverMuteBg;
            e.currentTarget.style.color = isLight ? "#0f172a" : "#ffffff";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = mute1;
          }}
        >
          <X size={13} />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-5 [scrollbar-width:thin]">
        <div className="mb-5 flex items-center gap-3.5">
          <span
            className="flex h-14 w-14 items-center justify-center rounded-2xl border font-['Space_Grotesk'] text-[19px] font-semibold"
            style={{
              background: isLight
                ? `linear-gradient(140deg, ${node.color}22, ${node.color}08)`
                : `linear-gradient(140deg, ${node.color}30, ${node.color}08)`,
              borderColor: `${node.color}55`,
              color: node.color,
              boxShadow: `0 8px 30px -8px ${node.color}50`,
            }}
          >
            {initials(node.name)}
          </span>
          <div className="min-w-0">
            <p
              className="truncate text-[15px] font-semibold"
              style={{ color: textPrimary }}
            >
              {node.name}
            </p>
            <p
              className="truncate text-[11.5px]"
              style={{ color: mute1 }}
            >
              {node.role}
            </p>
            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.2em]" style={{ color: node.color }}>
              {node.department}
            </p>
          </div>
        </div>

        <Field label="Full name" isLight={isLight} mute2={mute2}>
          <input
            ref={nameRef}
            style={{
              background: inputBg,
              borderColor: subBorder,
              color: textPrimary,
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = C.accentSoftEdge;
              e.currentTarget.style.background = inputBgFocus;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = subBorder;
              e.currentTarget.style.background = inputBg;
            }}
            className="w-full rounded-lg border px-3 py-2 text-[13px] outline-none transition-colors"
            value={node.name}
            onChange={(e) => onUpdate(node.id, { name: e.target.value })}
            placeholder="Full name"
          />
        </Field>

        <Field label="Role / Title" isLight={isLight} mute2={mute2}>
          <input
            style={{
              background: inputBg,
              borderColor: subBorder,
              color: textPrimary,
            }}
            onFocus={(e) => {
              e.currentTarget.style.borderColor = C.accentSoftEdge;
              e.currentTarget.style.background = inputBgFocus;
            }}
            onBlur={(e) => {
              e.currentTarget.style.borderColor = subBorder;
              e.currentTarget.style.background = inputBg;
            }}
            className="w-full rounded-lg border px-3 py-2 text-[13px] outline-none transition-colors"
            value={node.role}
            onChange={(e) => onUpdate(node.id, { role: e.target.value })}
            placeholder="Role"
          />
        </Field>

        <Field label="Team" isLight={isLight} mute2={mute2}>
          <div className="flex flex-wrap gap-1.5">
            {teams.map((t) => {
              const active = node.department === t.name;
              return (
                <button
                  key={t.name}
                  onClick={() => onUpdate(node.id, { department: t.name })}
                  className={cn(
                    "rounded-full border px-2.5 py-1 text-[10.5px] font-medium transition-all duration-200",
                    active
                      ? "text-black"
                      : ""
                  )}
                  style={
                    active
                      ? { background: t.color, borderColor: t.color }
                      : {
                          borderColor: isLight ? "rgba(15,23,42,0.12)" : "rgba(255,255,255,0.12)",
                          color: isLight ? "rgba(15,23,42,0.5)" : "rgba(255,255,255,0.5)",
                        }
                  }
                  onMouseEnter={(e) => {
                    if (!active) {
                      e.currentTarget.style.color = isLight
                        ? "rgba(15,23,42,0.85)"
                        : "rgba(255,255,255,0.85)";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!active) {
                      e.currentTarget.style.color = isLight
                        ? "rgba(15,23,42,0.5)"
                        : "rgba(255,255,255,0.5)";
                    }
                  }}
                >
                  {t.name}
                </button>
              );
            })}
          </div>
          <p
            className="mt-1.5 font-mono text-[8.5px] uppercase tracking-[0.18em]"
            style={{ color: mute2 }}
          >
            Manage teams in the legend ↙
          </p>
        </Field>

        <Field label="Accent" isLight={isLight} mute2={mute2}>
          <div className="flex items-center gap-2">
            {Array.from(new Set([...teams.map((t) => t.color), ...TEAM_PALETTE, ...EXTRA_SWATCHES]))
              .slice(0, 10)
              .map((c) => (
              <button
                key={c}
                aria-label={`Set accent ${c}`}
                onClick={() => onUpdate(node.id, { color: c })}
                className={cn(
                  "h-5 w-5 rounded-full transition-transform duration-150 hover:scale-125",
                  node.color === c && "ring-2 ring-offset-2"
                )}
                style={{
                  background: c,
                  ...(node.color === c
                    ? {
                        boxShadow: `0 0 0 2px ${
                          isLight ? "rgba(15,23,42,0.18)" : "rgba(255,255,255,0.7)"
                        } inset, 0 0 0 2px ${
                          isLight ? "rgba(15,23,42,0.18)" : "rgba(255,255,255,0.7)"
                        }`,
                        ["--tw-ring-color"]:
                          isLight ? "rgba(15,23,42,0.18)" : "rgba(255,255,255,0.7)",
                        ["--tw-ring-offset-color"]: ringOffset,
                        ["--tw-ring-offset-width"]: "2px",
                        ["--tw-ring-width"]: "2px",
                      }
                    : {}),
                }}
              />
            ))}
          </div>
        </Field>

        <Field label="Card size" isLight={isLight} mute2={mute2}>
          <div className="flex items-center gap-2.5">
            <input
              type="range"
              min={60}
              max={160}
              step={5}
              value={Math.round((node.scale ?? 1) * 100)}
              onChange={(e) =>
                onUpdate(node.id, { scale: Number(e.target.value) / 100 })
              }
              aria-label="Individual card size"
              className="flex-1"
              style={{
                accentColor: C.accent,
              }}
            />
            <span
              className="w-10 shrink-0 text-right font-mono text-[11px] tabular-nums"
              style={{ color: isLight ? "rgba(15,23,42,0.7)" : "rgba(255,255,255,0.7)" }}
            >
              {Math.round((node.scale ?? 1) * 100)}%
            </span>
            {node.scale !== undefined && node.scale !== 1 && (
              <button
                onClick={() => onUpdate(node.id, { scale: undefined })}
                aria-label="Reset to global card size"
                title="Reset to global card size"
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md border transition-colors"
                style={{
                  borderColor: isLight ? "rgba(15,23,42,0.1)" : "rgba(255,255,255,0.1)",
                  color: isLight ? "rgba(15,23,42,0.45)" : "rgba(255,255,255,0.45)",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = C.accentSoftEdge;
                  e.currentTarget.style.color = C.accent;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = isLight
                    ? "rgba(15,23,42,0.1)"
                    : "rgba(255,255,255,0.1)";
                  e.currentTarget.style.color = isLight
                    ? "rgba(15,23,42,0.45)"
                    : "rgba(255,255,255,0.45)";
                }}
              >
                <RotateCcw size={11} />
              </button>
            )}
          </div>
          <p
            className="mt-1.5 font-mono text-[8.5px] uppercase tracking-[0.18em]"
            style={{ color: mute2 }}
          >
            Relative to global size · chart adapts
          </p>
        </Field>

        <Field label="Reports to" isLight={isLight} mute2={mute2}>
          <select
            value={node.parentId ?? "__top__"}
            onChange={(e) =>
              onReparent(node.id, e.target.value === "__top__" ? null : e.target.value)
            }
            disabled={isOnlyRoot}
            style={{
              background: selectBg,
              color: textPrimary,
              borderColor: subBorder,
            }}
            onFocus={(e) => (e.currentTarget.style.borderColor = C.accentSoftEdge)}
            onBlur={(e) => (e.currentTarget.style.borderColor = subBorder)}
            className={cn(
              "w-full appearance-none rounded-lg border px-3 py-2 text-[13px] outline-none transition-colors",
              isOnlyRoot && "cursor-not-allowed opacity-60"
            )}
          >
            <option value="__top__">— Top level (CEO row) —</option>
            {parent && (
              <option value={parent.id}>
                {parent.name} — {parent.role}
              </option>
            )}
            {reparentOptions.map((n) => (
              <option key={n.id} value={n.id}>
                {n.name} — {n.role}
              </option>
            ))}
          </select>
          {isRoot && !isOnlyRoot && (
            <p
              className="mt-1.5 font-mono text-[8.5px] uppercase tracking-[0.18em]"
              style={{ color: mute2 }}
            >
              Peer of {rootIds.size - 1} other top-level card
              {rootIds.size - 1 > 1 ? "s" : ""}
            </p>
          )}
        </Field>

        <div className="mb-5 grid grid-cols-2 gap-2">
          <div
            className="rounded-lg border px-3 py-2.5"
            style={{
              borderColor: subBorder,
              background: chipInactiveBg,
            }}
          >
            <p
              className="font-['Space_Grotesk'] text-[16px] font-semibold"
              style={{ color: textPrimary }}
            >
              {reportCount}
            </p>
            <p
              className="font-mono text-[8.5px] uppercase tracking-[0.2em]"
              style={{ color: isLight ? "rgba(15,23,42,0.4)" : "rgba(255,255,255,0.35)" }}
            >
              Direct reports
            </p>
          </div>
          <button
            onClick={() => parent && onSelect(parent.id)}
            disabled={!parent}
            className="rounded-lg border px-3 py-2.5 text-left transition-colors disabled:opacity-50"
            style={{
              borderColor: subBorder,
              background: chipInactiveBg,
            }}
            onMouseEnter={(e) => {
              if (parent) e.currentTarget.style.borderColor = isLight ? "rgba(15,23,42,0.18)" : "rgba(255,255,255,0.15)";
            }}
            onMouseLeave={(e) => {
              if (parent) e.currentTarget.style.borderColor = subBorder;
            }}
          >
            <p
              className="truncate text-[11.5px] font-medium"
              style={{ color: isLight ? "rgba(15,23,42,0.8)" : "rgba(255,255,255,0.8)" }}
            >
              {parent ? parent.name : "—"}
            </p>
            <p
              className="font-mono text-[8.5px] uppercase tracking-[0.2em]"
              style={{ color: isLight ? "rgba(15,23,42,0.4)" : "rgba(255,255,255,0.35)" }}
            >
              Manager
            </p>
          </button>
        </div>

        <div className="space-y-2">
          <button
            onClick={() => onAddChild(node.id)}
            className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-[12.5px] font-semibold transition-all duration-200 hover:brightness-110 active:scale-[0.98]"
            style={{ background: C.accent, color: C.accentOnBg }}
          >
            <UserPlus size={14} strokeWidth={2.4} />
            Add direct report
          </button>
          {isRoot && (
            <button
              onClick={onAddRoot}
              className="flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-[12.5px] font-medium transition-all duration-200 active:scale-[0.98]"
              style={{
                borderColor: isLight ? "rgba(15,23,42,0.12)" : "rgba(255,255,255,0.12)",
                background: chipInactiveBg,
                color: isLight ? "rgba(15,23,42,0.8)" : "rgba(255,255,255,0.8)",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = C.accentSoftEdge;
                e.currentTarget.style.color = isLight ? "#0f172a" : "#ffffff";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = isLight
                  ? "rgba(15,23,42,0.12)"
                  : "rgba(255,255,255,0.12)";
                e.currentTarget.style.color = isLight
                  ? "rgba(15,23,42,0.8)"
                  : "rgba(255,255,255,0.8)";
              }}
            >
              <Crown size={14} />
              Add peer at this level
            </button>
          )}
          {!isOnlyRoot && (
            <button
              onClick={() => onRemove(node.id)}
              className="flex w-full items-center justify-center gap-2 rounded-xl border px-4 py-2.5 text-[12.5px] font-medium transition-all duration-200 active:scale-[0.98]"
              style={{
                borderColor: isLight
                  ? "rgba(239,68,68,0.25)"
                  : "rgba(255,107,107,0.25)",
                background: isLight
                  ? "rgba(239,68,68,0.07)"
                  : "rgba(255,107,107,0.07)",
                color: isLight ? "#dc2626" : "#ff8f8f",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = isLight
                  ? "rgba(239,68,68,0.14)"
                  : "rgba(255,107,107,0.14)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isLight
                  ? "rgba(239,68,68,0.07)"
                  : "rgba(255,107,107,0.07)";
              }}
            >
              <Trash2 size={14} />
              Remove {descendantIds.size > 1 ? `subtree (${descendantIds.size})` : "member"}
            </button>
          )}
        </div>
      </div>
    </motion.aside>
  );
}

function Field({ label, children, isLight, mute2 }) {
  return (
    <label className="mb-4 block">
      <span
        className="mb-1.5 block font-mono text-[9px] uppercase tracking-[0.25em]"
        style={{ color: mute2 }}
      >
        {label}
      </span>
      {children}
    </label>
  );
}
